import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Admin CRUD for CarrierDropoffLocation. Backend: ezx-server PR #225.
 * Customer-side read (active rows, distance-sorted from origin) lives
 * at /api/v2/carriers/:carrierId/dropoff-locations — not used here.
 */

export interface DropoffAddress {
  id?: string;
  line1: string;
  line2?: string | null;
  city: string;
  province: string;
  country: string;
  postalCode?: string | null;
  location?: { lat?: number; lng?: number };
}

export interface AdminDropoffLocation {
  id: string;
  carrierId: string;
  name: string;
  addressId: string;
  active: boolean;
  operatingHours?: unknown;
  providerRef?: string | null;
  address: DropoffAddress;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDropoffLocationInput {
  name: string;
  active?: boolean;
  address: {
    line1: string;
    line2?: string | null;
    city: string;
    province: string;
    country: string;
    postalCode?: string | null;
  };
  operatingHours?: unknown;
  providerRef?: string | null;
}

export interface UpdateDropoffLocationInput {
  name?: string;
  active?: boolean;
  address?: Partial<CreateDropoffLocationInput["address"]>;
  operatingHours?: unknown;
  providerRef?: string | null;
}

export function useGetAdminDropoffLocations(
  carrierId: string | undefined,
  options?: { includeInactive?: boolean }
) {
  const { token } = useSession();
  return useQuery({
    queryKey: [
      "admin-dropoff-locations",
      carrierId,
      options?.includeInactive ?? false,
    ],
    queryFn: async () => {
      if (!carrierId) return [];
      const params = options?.includeInactive ? "?includeInactive=true" : "";
      const { data } = await axios.get<{ data: AdminDropoffLocation[] }>(
        `${API_URL}/api/v2/admin/carriers/${carrierId}/dropoff-locations${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    enabled: !!carrierId && !!token,
  });
}

export function useCreateDropoffLocation(options?: {
  onSuccess?: (data: AdminDropoffLocation) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      carrierId,
      input,
    }: {
      carrierId: string;
      input: CreateDropoffLocationInput;
    }): Promise<AdminDropoffLocation> => {
      const { data } = await axios.post<{ data: AdminDropoffLocation }>(
        `${API_URL}/api/v2/admin/carriers/${carrierId}/dropoff-locations`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ["admin-dropoff-locations"] });
      options?.onSuccess?.(data);
    },
    onError: (err) => options?.onError?.(err),
  });
}

export function useUpdateDropoffLocation(options?: {
  onSuccess?: (data: AdminDropoffLocation) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: UpdateDropoffLocationInput;
    }): Promise<AdminDropoffLocation> => {
      const { data } = await axios.patch<{ data: AdminDropoffLocation }>(
        `${API_URL}/api/v2/admin/carrier-dropoff-locations/${id}`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ["admin-dropoff-locations"] });
      options?.onSuccess?.(data);
    },
    onError: (err) => options?.onError?.(err),
  });
}

export function useDeleteDropoffLocation(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<{ id: string }> => {
      const { data } = await axios.delete<{ data: { id: string } }>(
        `${API_URL}/api/v2/admin/carrier-dropoff-locations/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin-dropoff-locations"] });
      options?.onSuccess?.();
    },
    onError: (err) => options?.onError?.(err),
  });
}
